import { Group, Stack, createStyles } from '@mantine/core';
import { format } from 'date-fns';
import { Fragment, useMemo, useState, useCallback } from 'react';
import { IconMenu2, IconPencil } from '@tabler/icons-react';
import {
  DndContext,
  DragOverlay,
  MouseSensor,
  TouchSensor,
  useDraggable,
  useDroppable,
  useSensor,
  useSensors,
} from '@dnd-kit/core';
import { restrictToVerticalAxis, restrictToWindowEdges } from '@dnd-kit/modifiers';
import useTranslation from 'next-translate/useTranslation';

import dt from '~/domains/contest/domains/managev2/domains/common/testing.const';
import { Text } from '~/domains/design-system/Text';
import { InputModalFieldElement } from '~/domains/contest/domains/managev2/domains/common/fields/StaticField/InputModalField/InputModalField';
import type { InputModalField } from '~/domains/contest/domains/managev2/domains/common/fields/StaticField/InputModalField/types';

import type { SegmentsFieldItemType, SegmentsFieldType, PayoutSegmentsValue } from '../types';
import { pluralizeSlateLabel } from '../../../../utils/pluralizeSlateLabel';

const useEditStyles = createStyles((theme) => ({
  description: {
    display: 'block',
    margin: `${theme.spacing.xs}px 0`,
  },
  item: {
    borderRadius: theme.radius.md,
    padding: `${theme.spacing.md / 2}px ${theme.spacing.md}px`,
  },
  segmentDroppable: {
    minHeight: 44,
    borderRadius: theme.radius.md,
    padding: `${theme.spacing.xs}px 0`,
    transition: 'background-color 150ms ease',

    '&.isOver': {
      backgroundColor: theme.colors.blue[0],
    },
  },
  segment: {
    background: theme.colors.gray[7],
    color: theme.white,
    height: 44,
    position: 'relative',
    userSelect: 'none',
  },
  segmentLabelGroup: {
    gap: theme.other.spacing._12,

    '&.canEdit': {
      cursor: 'pointer',
    },
  },
  pencilIcon: {
    width: 20,
    height: 20,
    flexShrink: 0,
  },
  slate: {
    border: `1px solid ${theme.colors.gray[3]}`,
    userSelect: 'none',

    '&.canDrag': {
      cursor: 'grab',
    },
  },
  slateWrapper: {
    '&.isDragging': {
      opacity: 0.2,
    },
  },
  slateLabel: {
    flexShrink: 1,
  },
  slateDate: {
    flexShrink: 0,
  },
}));

function SegmentEditSlate({
  slate,
  canDrag = true,
}: {
  slate: SegmentsFieldItemType;
  canDrag?: boolean;
}) {
  const { classes, cx, theme } = useEditStyles();
  const {
    attributes,
    listeners,
    setNodeRef: setDraggableRef,
    isDragging,
  } = useDraggable({
    id: slate.key,
    disabled: !canDrag,
  });
  const { setNodeRef: setDroppableRef } = useDroppable({
    id: slate.key,
    disabled: !canDrag,
  });

  return (
    <div ref={setDroppableRef}>
      <div
        ref={setDraggableRef}
        {...(canDrag ? { ...attributes, ...listeners } : {})}
        className={cx(classes.slateWrapper, { isDragging })}
      >
        <Group
          key={slate.key}
          position="apart"
          className={cx(classes.item, classes.slate, { canDrag })}
          noWrap
          data-test-id={dt.segments.edit.slate}
        >
          <Group spacing="xs" noWrap>
            {canDrag && <IconMenu2 size={16} stroke={theme.colors.gray[7]} />}
            <Text variant="subhead-medium" className={classes.slateLabel}>
              {slate.label}
            </Text>
          </Group>
          <Text variant="body-large" className={classes.slateDate}>
            {format(slate.date, 'M/dd')}
          </Text>
        </Group>
      </div>
    </div>
  );
}

function SegmentEditSegment({
  segmentLabel,
  segmentIndex,
  allSegmentLabels,
  onLabelChange,
  segmentLabelField,
  canEditLabel = true,
}: {
  segmentLabel: string;
  segmentIndex: number;
  allSegmentLabels: string[];
  onLabelChange: (newLabel: string) => void;
  segmentLabelField: InputModalField;
  canEditLabel?: boolean;
}) {
  const { classes, cx, theme } = useEditStyles();
  const [isOpen, setIsOpen] = useState(false);

  const handleOpen = useCallback(() => {
    if (canEditLabel) {
      setIsOpen(true);
    }
  }, [canEditLabel]);

  const uniqueSegmentNameValidator = useMemo(
    () => createUniqueSegmentNameValidator(allSegmentLabels, segmentLabel),
    [allSegmentLabels, segmentLabel]
  );

  return (
    <>
      <Group
        className={cx(classes.item, classes.segment)}
        position="center"
        spacing="xs"
        data-test-id={dt.segments.edit.segment}
      >
        <Group
          spacing={0}
          className={cx(classes.segmentLabelGroup, { canEdit: canEditLabel })}
          onClick={handleOpen}
        >
          <Text variant="headline-medium">{segmentLabel}</Text>
          {canEditLabel && (
            <IconPencil className={classes.pencilIcon} stroke={2.5} color={theme.white} />
          )}
        </Group>
      </Group>

      {canEditLabel && (
        <InputModalFieldElement
          field={{
            ...segmentLabelField,
            key: `segment_label_${segmentIndex}`,
            validation: {
              ...segmentLabelField.validation,
              customValidator: uniqueSegmentNameValidator,
            },
          }}
          value={segmentLabel}
          onValueChange={onLabelChange}
          errors={undefined}
          isTouched={false}
          sectionKey=""
          allValues={{}}
          allErrors={{}}
          isOpen={isOpen}
          onOpenChange={setIsOpen}
        />
      )}
    </>
  );
}

// Helper function to create unique segment name validator
const createUniqueSegmentNameValidator =
  (allSegmentLabels: string[], currentLabel: string) =>
  (inputValue: string): string | null => {
    if (inputValue !== currentLabel && allSegmentLabels.includes(inputValue)) {
      return 'A segment with this name already exists';
    }
    return null;
  };

export function CustomSegmentsEdit({
  slates,
  rules,
  value,
  onChange,
  slateLabel = 'tournaments',
  segmentLabelField,
  canEditLabels = true,
  canEditSlates = true,
}: {
  slates: SegmentsFieldItemType[];
  rules: SegmentsFieldType['rules'];
  value: PayoutSegmentsValue;
  slateLabel?: string;
  segmentLabelField: InputModalField;
  onChange: (
    newValueOrCb: PayoutSegmentsValue | ((prev: PayoutSegmentsValue) => PayoutSegmentsValue)
  ) => void;
  canEditLabels?: boolean;
  canEditSlates?: boolean;
}) {
  const { classes, cx } = useEditStyles();
  const { t } = useTranslation('contest');
  const [activeSlateId, setActiveSlateId] = useState<string | null>(null);
  const [hoveredSegmentIndex, setHoveredSegmentIndex] = useState<number | null>(null);

  const mouseSensor = useSensor(MouseSensor);
  const touchSensor = useSensor(TouchSensor);
  const sensors = useSensors(mouseSensor, touchSensor);

  const slatesMap = useMemo(
    () =>
      slates.reduce((acc, slate) => {
        acc[slate.key] = slate;

        return acc;
      }, {} as Record<string, SegmentsFieldItemType>),
    [slates]
  );

  const sortedEntries = useMemo(
    () => Object.entries(value).sort(([, a], [, b]) => a.sortOrder - b.sortOrder),
    [value]
  );

  const allSegmentLabels = useMemo(() => sortedEntries.map(([, s]) => s.label), [sortedEntries]);

  const sortSlatesByDate = (slateIds: string[]): string[] =>
    [...slateIds].sort((a, b) => {
      const slateA = slatesMap[a];
      const slateB = slatesMap[b];
      if (!slateA || !slateB) return 0;
      return slateA.date.getTime() - slateB.date.getTime();
    });

  const findSegmentIdForSlate = (slateId: string): string | null => {
    const entry = sortedEntries.find(([, s]) => s.slateGroupIds.includes(slateId));
    return entry ? entry[0] : null;
  };

  const handleDragStart = (event) => {
    if (!canEditSlates) return;
    setActiveSlateId(event.active.id);
  };

  const handleDragOver = (event) => {
    if (!canEditSlates || !activeSlateId) return;

    const overId = event.over?.id;
    if (!overId) {
      setHoveredSegmentIndex(null);
      return;
    }

    const segmentId = findSegmentIdForSlate(overId);
    const segmentIndex = segmentId ? sortedEntries.findIndex(([id]) => id === segmentId) : -1;
    setHoveredSegmentIndex(segmentIndex >= 0 ? segmentIndex : null);
  };

  const handleDragEnd = (event) => {
    if (!canEditSlates) return;

    const overId = event.over?.id;

    if (activeSlateId && overId) {
      const sourceId = findSegmentIdForSlate(activeSlateId);
      const targetId = findSegmentIdForSlate(overId);

      if (
        sourceId &&
        targetId &&
        sourceId !== targetId &&
        value[sourceId].slateGroupIds.length > rules.minSlateGroupsPerSegment
      ) {
        onChange((segments) => ({
          ...segments,
          [sourceId]: {
            ...segments[sourceId],
            slateGroupIds: segments[sourceId].slateGroupIds.filter((id) => id !== activeSlateId),
          },
          [targetId]: {
            ...segments[targetId],
            slateGroupIds: sortSlatesByDate([...segments[targetId].slateGroupIds, activeSlateId]),
          },
        }));
      }
    }

    setActiveSlateId(null);
    setHoveredSegmentIndex(null);
  };

  const handleLabelChange = (segmentId: string, newLabel: string) => {
    if (value[segmentId].label === newLabel) return;

    onChange((segments) => ({
      ...segments,
      [segmentId]: { ...segments[segmentId], label: newLabel },
    }));
  };

  return (
    <DndContext
      onDragStart={handleDragStart}
      onDragEnd={handleDragEnd}
      onDragOver={handleDragOver}
      modifiers={[restrictToVerticalAxis]}
      sensors={sensors}
    >
      <div>
        <Text variant="body-medium" className={classes.description}>
          {t('manage.common.fields.customSegmentsEdit.description', {
            minCount: rules.minSlateGroupsPerSegment,
            slateLabel: slateLabel.toLowerCase(),
            slateLabelPlural: pluralizeSlateLabel(
              rules.minSlateGroupsPerSegment,
              slateLabel
            ).toLowerCase(),
          })}
        </Text>
        <Stack spacing="xs">
          {sortedEntries.map(([id, segment], index) => {
            const sortedSlateIds = sortSlatesByDate(segment.slateGroupIds);
            return (
              <Fragment key={id}>
                <SegmentEditSegment
                  segmentLabel={segment.label}
                  segmentIndex={index}
                  allSegmentLabels={allSegmentLabels}
                  onLabelChange={(newLabel) => handleLabelChange(id, newLabel)}
                  segmentLabelField={segmentLabelField}
                  canEditLabel={canEditLabels}
                />
                <Stack
                  className={cx(classes.segmentDroppable, {
                    isOver:
                      hoveredSegmentIndex === index && activeSlateId !== null && canEditSlates,
                  })}
                  spacing="xs"
                >
                  {sortedSlateIds.map((slateId) => {
                    const slate = slatesMap[slateId];
                    if (!slate) {
                      return null;
                    }
                    return <SegmentEditSlate key={slateId} slate={slate} canDrag={canEditSlates} />;
                  })}
                </Stack>
              </Fragment>
            );
          })}
        </Stack>
      </div>
      <DragOverlay modifiers={[restrictToWindowEdges]}>
        {activeSlateId ? <SegmentEditSlate slate={slatesMap[activeSlateId]} /> : null}
      </DragOverlay>
    </DndContext>
  );
}
